import {
  type IPropertyInfo,
  type IPaymentDateLegacy,
} from '~/@api/generated-api-types-project/Api'
import CompoundingYieldIcon from '~/@mid_ui/icons/CompoundingYieldIcon'
import type { ProjectsItem } from '../../_project_types/project.types'
import { cn } from '~/shadcn/lib/utils'
import { toNetYield } from '~/@pages_investing/fee-immunity/feeImmunityDisplay.utils'
import { useFeeImmunity } from '~/@pages_investing/fee-immunity/useFeeImmunity'
import { useRevenueFee } from '~/@pages_investing/_utils/revenue-fee/useRevenueFee'
import { ProjectPaymentTypeTooltip } from '../project-payment-type-label/ProjectPaymentTypeTooltip'
import { getProjectPaymentTypeLabel } from '../project-payment-type-label/getProjectPaymentTypeLabel'

export type ProjectWithSchemAndExtraYieldSettings = ProjectsItem & {
  scheme: {
    fixedYield: number
  }
  extraYieldSettings?: {
    maxPercentage: number
  }
  propertyInfo?: IPropertyInfo
  paymentDates: Array<IPaymentDateLegacy>
}

type Props = {
  _calculatedValues: ProjectsItem['_calculatedValues']
  isYieldCompoundingAnnually: boolean
  yieldIconSize: number
  yieldIconArrowColor?: string
  yieldPercentColor?: string
  className?: string
}

export const ProjectYieldText = ({
  _calculatedValues: { minYieldValue: rawMin, maxYieldValue: rawMax },
  isYieldCompoundingAnnually,
  yieldIconSize,
  yieldIconArrowColor,
  yieldPercentColor,
  className,
}: Props) => {
  const { hasFeeImmunity } = useFeeImmunity()
  const { fee } = useRevenueFee()

  const minYieldValue = toNetYield(rawMin, fee, hasFeeImmunity)
  const maxYieldValue = toNetYield(rawMax, fee, hasFeeImmunity)

  const tooltipContent = (function () {
    if (!isYieldCompoundingAnnually) return null

    return (
      <ProjectPaymentTypeTooltip
        paymentTypeLabel={getProjectPaymentTypeLabel('monthly', true)}
      />
    )
  })()

  if (maxYieldValue > 0) {
    return (
      <div
        className={cn(
          'flex flex-row items-center gap-1 flex-nowrap justify-end md:justify-start',
          className
        )}>
        {minYieldValue.toFixed(2).replace('.', ',')}
        <span>-</span>
        <YieldCompoundingValue
          value={maxYieldValue.toFixed(2).replace('.', ',')}
          isYieldCompounding={isYieldCompoundingAnnually}
          yieldIconSize={yieldIconSize}
          yieldIconArrowColor={yieldIconArrowColor}
          yieldPercentColor={yieldPercentColor}
        />
        <span>p.a.</span>
        {tooltipContent}
      </div>
    )
  }
  return (
    <div className={cn('flex flex-row gap-2', className)}>
      <YieldCompoundingValue
        value={minYieldValue.toFixed(2).replace('.', ',')}
        isYieldCompounding={isYieldCompoundingAnnually}
        yieldIconSize={yieldIconSize}
        yieldIconArrowColor={yieldIconArrowColor}
      />
      <span>p.a.</span>
      {tooltipContent}
    </div>
  )
}

export function YieldCompoundingValue(props: {
  value: string
  isYieldCompounding: boolean
  yieldIconSize: number
  yieldIconArrowColor?: string
  yieldPercentColor?: string
}) {
  if (!props.isYieldCompounding) return <>{`${props.value} %`}</>

  return (
    <div className="flex flex-row items-center gap-1">
      {props.value}
      <CompoundingYieldIcon
        size={props.yieldIconSize ?? 36}
        arrowColor={props.yieldIconArrowColor ?? '#fff'}
        percentColor={props.yieldPercentColor ?? '#74ff2e'}
      />
    </div>
  )
}
